import {
  Button,
  ButtonAlignment,
  ButtonWidth,
  Modal,
  TextAlign,
  useBreakpoint,
  useSession,
  useTranslation,
} from '@nextgen-web-framework/all';
import { identityApi } from '@commerce-identity-webcomponents/sdk';
import { useState } from 'react';
import { ConsoleLogger } from '@nextgen-web-framework/core';
import AccountError from '../account-error/AccountError';
import { ButtonLoader } from '../button-loader/ButtonLoader';
import { AccountData, MultiAccountSelectorProps } from './types/propTypes';
import { AccountRole, AccountStatus } from './types/enums';
import AccountListItem from './AccountListItem';
import styles from './AccountListItem.module.scss';

const MultiAccountSelector = ({ accountList, onClose, closeIcon, callback }: MultiAccountSelectorProps) => {
  const { session } = useSession();
  const [selectedAccountNumber, setSelectedAccountNumber] = useState<number | null>(
    session.aboNum as unknown as number,
  );
  const [showMultiAccountSelectorModal, setShowMultiAccountSelectorModal] = useState(true);
  const [accountErrorType, setAccountErrorType] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const sortedAccountList = accountList?.sort((firstAccount: AccountData, secondAccount: AccountData) => {
    const statusA =
      AccountStatus[firstAccount.accountStatus as unknown as keyof typeof AccountStatus] === AccountStatus.ACTIVE
        ? 0
        : 1;
    const statusB =
      AccountStatus[secondAccount.accountStatus as unknown as keyof typeof AccountStatus] === AccountStatus.ACTIVE
        ? 0
        : 1;
    return statusA - statusB;
  });

  const onClickHandler = async (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLoading(true);
    try {
      const res = await identityApi.switchAccount({
        accountNumber: selectedAccountNumber as number,
        errorHandlers: {
          400: async (err: Response) => {
            const errorRes = await err.json();
            const { subDetails } = errorRes;
            setShowMultiAccountSelectorModal(false);
            subDetails.length && setAccountErrorType(subDetails[0].message);
          },
        },
      });
      onClose?.(e);
      callback?.(res);
    } catch (error) {
      ConsoleLogger.error(error);
    }
  };

  const onCloseAccountErrorModal = () => {
    setIsLoading(false);
    setShowMultiAccountSelectorModal(true);
  };
  const viewPortType = useBreakpoint();
  const buttonWidth = ['xs', 'md', 'sm'].includes(viewPortType) ? ButtonWidth.FULL_WIDTH : ButtonWidth.FIT_CONTENT;
  const t = useTranslation();
  const headerTitle = t('identity.MultiAccountSelect.headerTitle');
  const buttonText = t('identity.MultiAccountSelect.buttonText');
  const getContent = () => (
    <div className={styles['identity-account-list']}>
      {sortedAccountList?.map((account: AccountData) => (
        <AccountListItem
          key={account.accountNumber}
          {...account}
          accountStatus={AccountStatus[account.accountStatus as unknown as keyof typeof AccountStatus]}
          setSelectedAccountNumber={setSelectedAccountNumber}
          selectedAccountNumber={selectedAccountNumber}
        />
      ))}
    </div>
  );
  const getProceedButton = () => (
    <>
      {isLoading && <ButtonLoader width={buttonWidth} componentClassName={buttonWidth} />}
      {!isLoading && (
        <Button
          data-testid="proceed-button-testid"
          title={buttonText}
          buttonWidth={buttonWidth}
          style={{
            width: buttonWidth === ButtonWidth.FIT_CONTENT ? '12.5rem' : '',
          }}
          onClick={onClickHandler}
        />
      )}
    </>
  );
  const multiAccountSelectorModalProps = {
    headline: headerTitle,
    textAlign: TextAlign.LEFT,
    primaryButtonLabel: '',
    primaryButton: getProceedButton(),
    buttonAlignment: ButtonAlignment.HORIZONTAL,
    children: getContent(),
    closeIcon: closeIcon,
    closeOnBackdropClick: false,
    onClose,
    useReactPortal: true,
    portalContainerId: 'react-portal-modal',
  };
  return showMultiAccountSelectorModal ? (
    <Modal {...multiAccountSelectorModalProps} />
  ) : (
    <AccountError callLogOut={false} errorType={accountErrorType} onCloseCallback={onCloseAccountErrorModal} />
  );
};

export default MultiAccountSelector;
export { AccountRole, AccountStatus };
